import {
  assertCanonicalString,
  concatBytes,
  getSubtleCrypto,
  normalizeSeed,
  sha256,
  utf8
} from "../../deal-protocol/src";
import type {
  SessionCodeConfig,
  SessionCodeInput
} from "./types";
import { SessionCodeError } from "./types";

const PREFIX = "YSI1.";
const DOMAIN = utf8("YSI-SESSION-CODE-V1\0");
const CHECKSUM_BYTES = 8;
const MAX_PAYLOAD_BYTES = 1024;
const UTF8_DECODER = new TextDecoder("utf-8", { fatal: true });

type PayloadTuple = readonly [
  1,
  string,
  "deal-v1",
  string,
  string,
  string,
  3 | 4 | 5,
  number,
  "zh" | "en"
];

function fail(
  code: ConstructorParameters<typeof SessionCodeError>[0],
  message: string
): never {
  throw new SessionCodeError(code, message);
}

function bytesToBinary(bytes: Uint8Array): string {
  let result = "";
  for (const byte of bytes) result += String.fromCharCode(byte);
  return result;
}

function binaryToBytes(value: string): Uint8Array {
  return Uint8Array.from(value, (character) => character.charCodeAt(0));
}

function base64UrlEncode(bytes: Uint8Array): string {
  return btoa(bytesToBinary(bytes))
    .replaceAll("+", "-")
    .replaceAll("/", "_")
    .replace(/=+$/u, "");
}

function base64UrlDecode(value: string): Uint8Array {
  if (!/^[A-Za-z0-9_-]+$/u.test(value) || value.length % 4 === 1) {
    fail("INVALID_BASE64URL", "Session code payload is not canonical base64url");
  }
  const padded = value.replaceAll("-", "+").replaceAll("_", "/").padEnd(
    Math.ceil(value.length / 4) * 4,
    "="
  );
  try {
    return binaryToBytes(atob(padded));
  } catch {
    fail("INVALID_BASE64URL", "Session code payload cannot be decoded");
  }
}

function assertVersionString(value: unknown, label: string): string {
  if (typeof value !== "string") {
    fail("INVALID_PAYLOAD", `${label} must be a string`);
  }
  try {
    assertCanonicalString(value, label);
  } catch {
    fail("INVALID_PAYLOAD", `${label} is not a canonical string`);
  }
  return value;
}

function configToTuple(config: SessionCodeConfig): PayloadTuple {
  return [
    1,
    config.rulesVersion,
    config.dealVersion,
    config.matcherVersion,
    config.catalogRevision,
    config.normalizedSeed,
    config.playerCount,
    config.round,
    config.contentLocale
  ];
}

function tupleToConfig(value: unknown): SessionCodeConfig {
  if (!Array.isArray(value) || value.length !== 9) {
    fail("INVALID_PAYLOAD", "Session code payload must be a nine-field tuple");
  }
  const [schema, rules, deal, matcher, catalog, seed, count, round, locale] =
    value as unknown[];
  if (schema !== 1) {
    fail("UNSUPPORTED_VERSION", `Unsupported session code schema: ${String(schema)}`);
  }
  if (deal !== "deal-v1") {
    fail("UNSUPPORTED_VERSION", `Unsupported deal protocol: ${String(deal)}`);
  }
  if (count !== 3 && count !== 4 && count !== 5) {
    fail("INVALID_PAYLOAD", "playerCount must be 3, 4, or 5");
  }
  if (!Number.isInteger(round) || (round as number) < 1 || (round as number) > 0xffff_ffff) {
    fail("INVALID_PAYLOAD", "round must be an integer in 1..2^32-1");
  }
  if (locale !== "zh" && locale !== "en") {
    fail("INVALID_PAYLOAD", "contentLocale must be zh or en");
  }
  const normalizedSeed = assertVersionString(seed, "normalizedSeed");
  if (normalizeSeed(normalizedSeed) !== normalizedSeed) {
    fail("INVALID_PAYLOAD", "normalizedSeed is not in canonical seed form");
  }
  return {
    schemaVersion: "session-code-v1",
    rulesVersion: assertVersionString(rules, "rulesVersion"),
    dealVersion: "deal-v1",
    matcherVersion: assertVersionString(matcher, "matcherVersion"),
    catalogRevision: assertVersionString(catalog, "catalogRevision"),
    normalizedSeed,
    playerCount: count,
    round: round as number,
    contentLocale: locale
  };
}

function normalizeInput(input: SessionCodeInput): SessionCodeConfig {
  return tupleToConfig([
    1,
    input.rulesVersion,
    input.dealVersion,
    input.matcherVersion,
    input.catalogRevision,
    normalizeSeed(input.rawSeed),
    input.playerCount,
    input.round,
    input.contentLocale
  ]);
}

async function checksum(
  payload: Uint8Array,
  subtle: SubtleCrypto
): Promise<Uint8Array> {
  return (await sha256(concatBytes(DOMAIN, payload), subtle)).slice(
    0,
    CHECKSUM_BYTES
  );
}

function equalBytes(left: Uint8Array, right: Uint8Array): boolean {
  if (left.length !== right.length) return false;
  let difference = 0;
  for (let index = 0; index < left.length; index += 1) {
    difference |= left[index]! ^ right[index]!;
  }
  return difference === 0;
}

export async function encodeSessionCode(
  input: SessionCodeInput,
  subtle: SubtleCrypto = getSubtleCrypto()
): Promise<string> {
  const config = normalizeInput(input);
  const payload = utf8(JSON.stringify(configToTuple(config)));
  if (payload.length > MAX_PAYLOAD_BYTES) {
    fail("PAYLOAD_TOO_LARGE", "Session code payload exceeds 1024 bytes");
  }
  const integrity = await checksum(payload, subtle);
  return `${PREFIX}${base64UrlEncode(concatBytes(payload, integrity))}`;
}

export async function decodeSessionCode(
  code: string,
  subtle: SubtleCrypto = getSubtleCrypto()
): Promise<SessionCodeConfig> {
  const compact = code.trim();
  if (!compact.startsWith(PREFIX)) {
    fail("INVALID_FORMAT", `Session code must start with ${PREFIX}`);
  }
  const envelope = base64UrlDecode(compact.slice(PREFIX.length));
  if (envelope.length <= CHECKSUM_BYTES) {
    fail("INVALID_FORMAT", "Session code contains no payload");
  }
  const payload = envelope.slice(0, -CHECKSUM_BYTES);
  if (payload.length > MAX_PAYLOAD_BYTES) {
    fail("PAYLOAD_TOO_LARGE", "Session code payload exceeds 1024 bytes");
  }
  const actual = envelope.slice(-CHECKSUM_BYTES);
  const expected = await checksum(payload, subtle);
  if (!equalBytes(actual, expected)) {
    fail("CHECKSUM_MISMATCH", "Session code checksum does not match");
  }
  let decoded: unknown;
  try {
    decoded = JSON.parse(UTF8_DECODER.decode(payload)) as unknown;
  } catch {
    fail("INVALID_PAYLOAD", "Session code payload is not valid UTF-8 JSON");
  }
  return tupleToConfig(decoded);
}

export function sessionCodeFragment(code: string): string {
  if (!code.startsWith(PREFIX)) {
    fail("INVALID_FORMAT", `Session code must start with ${PREFIX}`);
  }
  return `#ysi=${encodeURIComponent(code)}`;
}

export function sessionCodeFromFragment(fragment: string): string | null {
  const raw = fragment.startsWith("#") ? fragment.slice(1) : fragment;
  const code = new URLSearchParams(raw).get("ysi");
  return code === null || code.length === 0 ? null : code;
}
